import React from 'react'
import { Control, Controller, FieldErrors, UseFormSetValue, UseFormWatch, useWatch } from 'react-hook-form'
import { c, t } from 'ttag'
import { FormData } from '../../ChallengeAdmin'
import { CustomTypeCategory, customTypes, customUnits } from '../../../../services/CustomTypes'
import { isSimpleDayType } from '../../../../common/helpers/challenges'
import YumAccordion from '../../../../components/YumAccordion/YumAccordion'
import YumAccordionSummary from '../../../../components/YumAccordionSummary/YumAccordionSummary'
import Typography from '@material-ui/core/Typography'
import { isRtl } from '../../../../i18nInit'
import { AccordionDetails, IconButton } from '@material-ui/core'
import { ReactComponent as TotalTargetInactiveIcon } from '../../../../assets/v3/icons/total-target-inactive.svg'
import YumRemoveButton from '../../../../components/YumRemoveButton/YumRemoveButton'
import { ReactComponent as RemoveIcon } from '../../../../assets/v3/icons/remove.svg'

// Import shared helpers
import { useDailyGoalUnit, useTotalGoalEndAdornment } from './goalUnitHelpers'
import { getTotalAmountGoalValidationRules } from './goalValidationHelpers'

// Import field components
import TotalGoalField from './fields/TotalGoalField'
import IndexTargetField from './fields/IndexTargetField'
import ErrorMessage from '../../../../components/ErrorMessage/ErrorMessage'
import YumSelect from '../../../../components/YumSelect/YumSelect'

interface GoalAccordionProps {
  control: Control<FormData>
  watch: UseFormWatch<FormData>
  setValue: UseFormSetValue<FormData>
  errors: FieldErrors<FormData>
  activityType: CustomTypeCategory
  formStep: number
  // Field name prefix for nested goals (e.g., 'teamGoal', 'groupGoals.categoryId', 'countryGoal')
  fieldPrefix: string
  hideRemove?: boolean
}

/**
 * Total amount goal accordion component.
 * Renders a total amount goal accordion with appropriate input fields.
 * Allows users to set total goals, daily max, and unit preferences.
 */
export default function TotalAmountGoal(props: GoalAccordionProps) {
  const { control, watch, setValue, errors, activityType, formStep, fieldPrefix, hideRemove = false } = props

  // Watch form values needed for the component
  // Use useWatch hook for better reactivity - ensures component re-renders when values change
  const goalType = useWatch({ control, name: 'goalType' })
  const customUnit = useWatch({ control, name: 'customUnit' })
  const customType = useWatch({ control, name: 'customType' })
  const simpleManualEntry = useWatch({ control, name: 'simpleManualEntry' })

  // Check if this is a defined custom activity type
  const isDefinedCustomActivity = customTypes.some((ct) => ct.id === activityType)

  // Get validation rules for all fields
  const validationRules = getTotalAmountGoalValidationRules(
    formStep,
    activityType,
    goalType,
    isDefinedCustomActivity,
    simpleManualEntry,
  )

  // Calculate unit texts using custom hooks
  const dailyGoalUnit = useDailyGoalUnit(activityType, customType ?? null, customUnit ?? '')
  const customUnitFieldWithText = customUnits.find((cu) => cu.id === customType)?.text
  const totalGoalEndAdornment = useTotalGoalEndAdornment(activityType, customUnit ?? '', customUnitFieldWithText)
  const dailyMaxEndAdornment = useTotalGoalEndAdornment(activityType, customUnit ?? '', customUnitFieldWithText)

  // Determine which fields to show
  // const showCustomUnitInput = shouldShowCustomUnitInput(activityType, simpleManualEntry)
  // const dailyGoalEnabled = isDailyGoalEnabled(formStep, activityType, goalType, simpleManualEntry)

  // Don't render if it's a simple day type
  if (isSimpleDayType(activityType as CustomTypeCategory)) {
    return null
  }

  // Get the current goal for this specific goal
  // When fieldPrefix is empty, use 'goals.individual' as the path for individual goals
  const goalPath = fieldPrefix || 'goals.individual'
  // Use useWatch for better reactivity
  const currentGoal = useWatch({ control, name: goalPath as any })

  // Handler to remove/disable the goal
  const handleRemoveGoal = () => {
    if (currentGoal && currentGoal.enabled) {
      setValue(goalPath as any, { ...currentGoal, enabled: false })
    }
  }

  // Build custom total amount inputs (for non-ALL activity types)
  const customTotalAmountInputs = (
    <>
      {/* Custom unit field or input based on activity type */}
      {/* {
        activityType !== CustomTypeCategory.POINTS &&
          (isDefinedCustomActivity ? (
            <CustomUnitField
              control={control}
              fieldPrefix={fieldPrefix}
              activityType={activityType}
              formStep={formStep}
              validationRules={validationRules.customType}
            />
          ) : (
          showCustomUnitInput && (
            <CustomUnitInput control={control} fieldPrefix={fieldPrefix} validationRules={validationRules.customUnit} />
          )
        )
      )} */}

      {/* Total goal field */}
      <TotalGoalField
        control={control}
        fieldPrefix={fieldPrefix}
        validationRules={validationRules.totalGoal}
        endAdornment={totalGoalEndAdornment}
      />

      {/* Daily max field (shown when daily goal is enabled) */}
      {/* {dailyGoalEnabled && (
        <DailyMaxField
          control={control}
          fieldPrefix={fieldPrefix}
          validationRules={{
            min: dailyGoalEnabled ? 1 : undefined,
            max: dailyGoalEnabled ? 1000000 : undefined,
          }}
          endAdornment={dailyMaxEndAdornment}
        />
      )} */}
    </>
  )

  // Index total amount inputs (for ALL activity type)
  const indexTotalAmountInputs = (
    <IndexTargetField
      control={control}
      fieldPrefix={fieldPrefix}
      validationRules={validationRules.indexTarget}
      unitText={dailyGoalUnit}
    />
  )

  return (
    // <YumAccordion expanded={true}>
    //   <YumAccordionSummary
    //     expandIcon={null}
    //     style={{
    //       justifyContent: 'space-between',
    //     }}
    //   >
    //     <TotalTargetInactiveIcon />
    //     <Typography
    //       variant="body2"
    //       style={isRtl ? { marginRight: '10px', flexGrow: 1 } : { marginLeft: '10px', flexGrow: 1 }}
    //     >
    //       {t`Total target`}
    //     </Typography>
    //     <YumRemoveButton
    //       onClick={handleRemoveGoal}
    //       style={{
    //         flexShrink: 0,
    //         marginRight: '-7px',
    //         padding: '8px',
    //       }}
    //     />
    //   </YumAccordionSummary>
    //   <AccordionDetails
    //     style={
    //       activityType === CustomTypeCategory.ALL
    //         ? {
    //             paddingBottom: '0px',
    //           }
    //         : {}
    //     }
    //   >
    <>
      {/* <YumRemoveButton
        onClick={handleRemoveGoal}
        style={{
          flexShrink: 0,
          marginRight: '-7px',
          padding: '8px',
        }}
      /> */}
      <div
        style={{
          flexBasis: '100%',
          //flexGrow: 1,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            //marginRight: isRtl ? 0 : 48,
            //marginLeft: !isRtl ? 0 : 48,
          }}
        >
          <div
            style={{
              //marginRight: isRtl ? 'unset' : '10px',
              //marginLeft: !isRtl ? 'unset' : '10px',
              flexGrow: 1,
            }}
          >
            {activityType !== CustomTypeCategory.ALL && customTotalAmountInputs}
            {activityType === CustomTypeCategory.ALL && indexTotalAmountInputs}
          </div>
          {!hideRemove && (
            <IconButton
              style={{
                flexShrink: 0,
                marginTop: '2px',
                position: 'relative',
                right: isRtl ? 'unset' : '-10px',
                left: !isRtl ? 'unset' : '-10px',
              }}
              aria-label="delete"
              //onClick={() => remove(index)}
              onClick={handleRemoveGoal}
            >
              <RemoveIcon
                style={{
                  width: 24,
                  height: 24,
                }}
              />
            </IconButton>
          )}
        </div>
      </div>
      {/* </AccordionDetails> */}
      {/* </YumAccordion> */}
    </>
  )
}
